import create from 'zustand'
import { persist } from 'zustand/middleware'

type ShowOnceState = {
  flags: Record<string, boolean>
  markAsShown: (key: string) => void
  reset: (key: string) => void
}

export const useShowOnceStore = create<ShowOnceState>(
  persist(
    (set) => ({
      flags: {},
      markAsShown: (key) =>
        set((state) => ({ flags: { ...state.flags, [key]: true } })),
      reset: (key) =>
        set((state) => {
          const flags = { ...state.flags }
          delete flags[key]
          return { flags }
        }),
    }),
    { name: 'show-once-flags' }
  )
)

/**
 * Tracks whether something has been shown to the user before, persisted in localStorage via Zustand.
 *
 * @param key - Unique key for this flag
 * @returns `{ hasBeenShown, markAsShown, reset }`
 *
 * @example
 * const { hasBeenShown, markAsShown } = useShowOnce('onboarding-banner')
 * if (!hasBeenShown) return <Banner onClose={markAsShown} />
 */
export function useShowOnce(key: string) {
  const hasBeenShown = useShowOnceStore((state) => state.flags[key] ?? false)
  const markAsShown = useShowOnceStore((state) => state.markAsShown)
  const reset = useShowOnceStore((state) => state.reset)

  return {
    hasBeenShown,
    markAsShown: () => markAsShown(key),
    reset: () => reset(key),
  }
}
